import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { Switch } from "../components/Switch";

import {
  CONSENT_TEMPLATES,
  CUSTOM_CONSENT_TEMPLATE_ID,
  findConsentTemplate,
  suggestNextConsentVersion,
} from "../data/consent-templates";
import {
  consentAckMemberLabel,
  CONSENT_ACKS_UNAVAILABLE,
  fetchFieldConsentAcks,
  formatConsentAckedAt,
  splitConsentAckRoster,
  summarizeConsentAcks,
  type ConsentAckLoad,
  type ConsentAckRow,
} from "../data/consent-acks";
import {
  fetchConsentPolicyVersions,
  publishConsentPolicyVersion,
  type ConsentPolicyDraft,
  type ConsentPolicyVersion,
} from "../data/operator-controls";
import { useReadModel } from "../data/useReadModel";

/** The version officers are being asked to accept right now, or null when none is published. */
export function activeConsentVersion(versions: readonly ConsentPolicyVersion[]): string | null {
  return versions.find((version) => version.active && version.retiredAt === null)?.consentVersion ?? null;
}

export function ConsentPolicyPanel({
  client,
  accountId,
  canView = false,
  canManage = false,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canView?: boolean;
  canManage?: boolean;
  pollMs?: number;
}) {
  if (!canView) {
    return null;
  }
  return (
    <ConsentPolicyPanelInner
      client={client}
      accountId={accountId}
      canManage={canManage}
      {...(pollMs !== undefined ? { pollMs } : {})}
    />
  );
}

function ConsentPolicyPanelInner({
  client,
  accountId,
  canManage,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  pollMs?: number;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const { status, data, error } = useReadModel<ConsentPolicyVersion[]>(
    () => fetchConsentPolicyVersions(client, accountId),
    [client, accountId, reloadNonce],
    pollMs !== undefined ? { pollMs } : {},
  );

  return (
    <div className="operator-panel">
      {canManage ? (
        <ConsentPolicyEditor
          client={client}
          accountId={accountId}
          existingVersions={data ?? []}
          onSaved={() => setReloadNonce((nonce) => nonce + 1)}
        />
      ) : null}

      {status === "loading" && data === null ? (
        <p className="surface__state" role="status">Loading consent versions...</p>
      ) : status === "error" ? (
        <p className="surface__state surface__state--error" role="alert">
          {error ?? "Could not load consent policy versions."}
        </p>
      ) : (
        <ConsentVersionList versions={data ?? []} />
      )}

      <ConsentAckRoster
        client={client}
        accountId={accountId}
        activeVersion={activeConsentVersion(data ?? [])}
        {...(pollMs !== undefined ? { pollMs } : {})}
      />
    </div>
  );
}

/**
 * Who has accepted the version now in force. Thin by design: name (or email), the version they
 * accepted, and when. Members still owing an acceptance are listed first, because that is the only
 * part of this an operator can act on.
 */
function ConsentAckRoster({
  client,
  accountId,
  activeVersion,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  activeVersion: string | null;
  pollMs?: number;
}) {
  const { status, data, error } = useReadModel<ConsentAckLoad>(
    () => fetchFieldConsentAcks(client, accountId),
    [client, accountId],
    pollMs !== undefined ? { pollMs } : {},
  );

  if (status === "loading" && data === null) {
    return (
      <section className="consent-acks" aria-label="Field consent acceptances">
        <h4 className="consent-acks__head">Who has accepted</h4>
        <p className="surface__state" role="status">Loading acceptances...</p>
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className="consent-acks" aria-label="Field consent acceptances">
        <h4 className="consent-acks__head">Who has accepted</h4>
        <p className="surface__state surface__state--error" role="alert">
          {error ?? "Could not load field consent acceptances."}
        </p>
      </section>
    );
  }

  // The host read path is not deployed here. Say that, rather than showing an empty roster that
  // would read as "nobody in this workspace has accepted".
  if (data === null || data.status === "unavailable") {
    return (
      <section className="consent-acks" aria-label="Field consent acceptances">
        <h4 className="consent-acks__head">Who has accepted</h4>
        <p className="surface__state" role="status">{CONSENT_ACKS_UNAVAILABLE}</p>
      </section>
    );
  }

  // Host returns current version on every roster row; version-list is only a fallback when empty.
  const version = data.activeVersion ?? activeVersion;
  const roster = splitConsentAckRoster(data.rows);

  return (
    <section className="consent-acks" aria-label="Field consent acceptances">
      <h4 className="consent-acks__head">Who has accepted</h4>
      <p className="consent-acks__summary" role="status">{summarizeConsentAcks(roster, version)}</p>
      {roster.accepted.length === 0 && roster.missing.length === 0 ? null : (
        <ul className="operator-list consent-acks__list" aria-label="Field consent acceptance roster">
          {roster.missing.map((row) => (
            <ConsentAckItem key={row.membershipId} row={row} accepted={false} />
          ))}
          {roster.accepted.map((row) => (
            <ConsentAckItem key={row.membershipId} row={row} accepted />
          ))}
        </ul>
      )}
    </section>
  );
}

function ConsentAckItem({ row, accepted }: { row: ConsentAckRow; accepted: boolean }) {
  return (
    <li className="operator-row operator-row--stacked">
      <div className="operator-row__line">
        <span className="operator-row__title">{consentAckMemberLabel(row)}</span>
        <span className={`rollout__badge ${accepted ? "rollout__badge--ready" : "rollout__badge--disabled"}`}>
          {accepted ? "Accepted" : "Not accepted"}
        </span>
      </div>
      <div className="operator-row__meta">
        {accepted
          ? `${row.consentVersion ?? "Accepted"} · ${formatConsentAckedAt(row.acceptedAt)}`
          : `${row.consentVersion ? `Owes ${row.consentVersion}` : "No active policy"} · ${formatConsentAckedAt(null)}`}
      </div>
    </li>
  );
}

function ConsentPolicyEditor({
  client,
  accountId,
  existingVersions,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  existingVersions: readonly ConsentPolicyVersion[];
  onSaved: () => void;
}) {
  const defaultTemplate = CONSENT_TEMPLATES[0]!;
  const [templateId, setTemplateId] = useState(defaultTemplate.id);
  const [customBody, setCustomBody] = useState("");
  const [draft, setDraft] = useState<ConsentPolicyDraft>({
    bodyText: defaultTemplate.bodyText,
    consentVersion: defaultTemplate.consentVersion,
    title: defaultTemplate.title,
  });
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  function applyTemplate(id: string): void {
    setTemplateId(id);
    setFailure(null);
    setReceipt(null);
    if (id === CUSTOM_CONSENT_TEMPLATE_ID) {
      const nextVersion = suggestNextConsentVersion(existingVersions.map((row) => row.consentVersion));
      setDraft({
        consentVersion: nextVersion,
        title: "Custom on-duty notice",
        bodyText: customBody,
      });
      return;
    }
    const template = findConsentTemplate(id);
    if (template === null) {
      return;
    }
    setDraft({
      consentVersion: template.consentVersion,
      title: template.title,
      bodyText: template.bodyText,
    });
  }

  function updateCustomBody(value: string): void {
    setCustomBody(value);
    if (templateId === CUSTOM_CONSENT_TEMPLATE_ID) {
      setDraft((current) => ({ ...current, bodyText: value }));
    }
  }

  async function submit(): Promise<void> {
    if (busy) return;
    if (!confirmed) {
      setFailure("Confirm that officers must re-accept this consent policy.");
      return;
    }
    if (draft.bodyText.trim().length === 0) {
      setFailure("Consent body is required.");
      return;
    }

    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const outcome = await publishConsentPolicyVersion(client, accountId, {
      ...draft,
      bodyText: draft.bodyText.trim(),
      consentVersion: draft.consentVersion.trim(),
      title: draft.title.trim(),
    });
    if (outcome.ok) {
      const resetTemplate = CONSENT_TEMPLATES[0]!;
      setTemplateId(resetTemplate.id);
      setCustomBody("");
      setDraft({
        bodyText: resetTemplate.bodyText,
        consentVersion: resetTemplate.consentVersion,
        title: resetTemplate.title,
      });
      setConfirmed(false);
      setReceipt("Consent policy published.");
      onSaved();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <form
      className="policy__editor operator-consent"
      aria-label="Publish field duty consent"
      onSubmit={(event) => event.preventDefault()}
    >
      <fieldset className="consent-templates">
        <legend className="consent-templates__legend">Policy template</legend>
        <div className="consent-templates__grid" role="radiogroup" aria-label="Consent template">
          {CONSENT_TEMPLATES.map((template) => (
            <label
              key={template.id}
              className={`consent-templates__card ${templateId === template.id ? "consent-templates__card--active" : ""}`}
            >
              <input
                type="radio"
                name="consent-template"
                value={template.id}
                checked={templateId === template.id}
                onChange={() => applyTemplate(template.id)}
              />
              <span className="consent-templates__title">{template.title}</span>
              <span className="consent-templates__desc">{template.description}</span>
            </label>
          ))}
          <label
            className={`consent-templates__card ${templateId === CUSTOM_CONSENT_TEMPLATE_ID ? "consent-templates__card--active" : ""}`}
          >
            <input
              type="radio"
              name="consent-template"
              value={CUSTOM_CONSENT_TEMPLATE_ID}
              checked={templateId === CUSTOM_CONSENT_TEMPLATE_ID}
              onChange={() => applyTemplate(CUSTOM_CONSENT_TEMPLATE_ID)}
            />
            <span className="consent-templates__title">Custom wording</span>
            <span className="consent-templates__desc">Legal-reviewed copy not in the catalog yet.</span>
          </label>
        </div>
      </fieldset>

      <div className="tpl__field-row">
        <label className="tpl__field">
          Version
          <input
            value={draft.consentVersion}
            onChange={(event) => setDraft((current) => ({ ...current, consentVersion: event.target.value }))}
            placeholder="field-duty-standard-v1"
          />
        </label>
        <label className="tpl__field">
          Title
          <input
            value={draft.title}
            onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
          />
        </label>
      </div>

      {templateId === CUSTOM_CONSENT_TEMPLATE_ID ? (
        <label className="tpl__field">
          Custom body
          <textarea value={customBody} onChange={(event) => updateCustomBody(event.target.value)} />
        </label>
      ) : (
        <div className="consent-preview" aria-label="Consent preview">
          <p className="consent-preview__label">Officers will see</p>
          <div className="consent-preview__body">{draft.bodyText}</div>
        </div>
      )}

      <Switch
        checked={confirmed}
        onChange={setConfirmed}
        tone="warn"
        label="Officers must re-accept after activation"
        hint="Guards see the new consent on next duty start and must accept before pings resume."
      />
      <div className="policy__editor-actions">
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
          {busy ? "Publishing..." : "Publish consent"}
        </button>
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}

function ConsentVersionList({ versions }: { versions: readonly ConsentPolicyVersion[] }) {
  if (versions.length === 0) {
    return <p className="surface__state" role="status">No field duty consent versions.</p>;
  }

  return (
    <ul className="operator-list consent-history" aria-label="Field duty consent versions">
      {versions.map((version) => (
        <li key={version.id} className="operator-row operator-row--stacked">
          <div className="operator-row__line">
            <span className="operator-row__title">{version.title}</span>
            <span className={`rollout__badge ${version.active ? "rollout__badge--ready" : "rollout__badge--disabled"}`}>
              {version.active ? "Active" : "Retired"}
            </span>
          </div>
          <div className="operator-row__meta">
            {version.consentVersion} · {version.locationScope} · {new Date(version.effectiveAt).toLocaleDateString()}
          </div>
        </li>
      ))}
    </ul>
  );
}
